import { CalendarDate, formattedDateTimeDefault } from "@sp-crm/core";

interface ScheduledVisitSource {
    id: string;
    eventDate: string;
    eventTime?: string | null;
    outcome?: string | null;
}

export interface TaskDueSource {
    dueDate?: string | null;
    dueDateTime?: string | null;
}

interface ScheduledTaskSource extends TaskDueSource {
    id: string;
    done: boolean;
}

export interface ScheduledVisitRow<TVisit> {
    key: string;
    when: Date;
    kind: "visit";
    awaitingOutcome: boolean;
    visit: TVisit;
}

export interface ScheduledTaskRow<TTask> {
    key: string;
    when: Date | null;
    kind: "task";
    task: TTask;
}

export type ScheduledActivityRow<TVisit, TTask> =
    | ScheduledVisitRow<TVisit>
    | ScheduledTaskRow<TTask>;

export const taskDueMoment = (task: TaskDueSource): Date | null => {
    if (task.dueDateTime) {
        return new Date(task.dueDateTime);
    }
    if (task.dueDate) {
        return new Date(`${task.dueDate}T23:59`);
    }
    return null;
};

export const taskDueLabel = (task: TaskDueSource): string => {
    if (task.dueDate) {
        const parsed = CalendarDate.parse(task.dueDate);
        if (parsed.hasValue) {
            return `Due ${parsed.get().humanFriendly()}`;
        }
    }
    if (task.dueDateTime) {
        return `Due ${formattedDateTimeDefault(task.dueDateTime)}`;
    }
    return "No due date";
};

const dueSortValue = (when: Date | null): number =>
    when?.getTime() ?? Number.POSITIVE_INFINITY;

export const sortTasksByDue = <T extends TaskDueSource>(tasks: T[]): T[] =>
    [...tasks].sort(
        (a, b) => dueSortValue(taskDueMoment(a)) - dueSortValue(taskDueMoment(b)),
    );

interface ScheduledActivityBounds {
    now: Date;
    keepCompletedIds: string[];
}

export const buildScheduledActivityRows = <
    TVisit extends ScheduledVisitSource,
    TTask extends ScheduledTaskSource,
>(
    visits: TVisit[],
    tasks: TTask[],
    bounds: ScheduledActivityBounds,
): ScheduledActivityRow<TVisit, TTask>[] => {
    const { now, keepCompletedIds } = bounds;
    const rows: ScheduledActivityRow<TVisit, TTask>[] = [];

    visits
        .filter(v => !v.outcome?.trim())
        .forEach(v => {
            const when = new Date(`${v.eventDate}T${v.eventTime ?? "23:59"}`);
            rows.push({
                key: `visit-${v.id}`,
                when,
                kind: "visit",
                awaitingOutcome: when.getTime() < now.getTime(),
                visit: v,
            });
        });

    tasks
        .filter(t => !t.done || keepCompletedIds.includes(t.id))
        .forEach(t => {
            rows.push({
                key: `task-${t.id}`,
                when: taskDueMoment(t),
                kind: "task",
                task: t,
            });
        });

    return rows.sort((a, b) => dueSortValue(a.when) - dueSortValue(b.when));
};

export const isScheduledTaskOverdue = (when: Date | null, now: Date): boolean => {
    return when !== null && when.getTime() < now.getTime();
};
